import React from 'react';
import { IconButton } from '@material-ui/core';
import { useSnackbar, VariantType, OptionsObject } from 'notistack';
import {
    TPayload,
    TTicketPayload,
    TNotificationOptions,
} from 'src/components/Alerts/types';
import { ExternalLink } from 'react-feather';
import { useHistory } from 'react-router-dom';
import { getSnackbarProps } from '../utils/getSnackbarProps';
import { useInteraction } from 'src/hooks/useInteraction';
import { useTranslation } from 'react-i18next';

export const SnackbarAlert = () => {
    const { enqueueSnackbar } = useSnackbar();
    const history = useHistory();
    const hasUserInteracted = useInteraction();
    const { t } = useTranslation();

    const playNotificationSound = async () => {
        if (hasUserInteracted) {
            try {
                const audio = new Audio('/static/notification.mp3');
                await audio.play();
            } catch (err) {
                enqueueSnackbar(t('Errors.NoAudio'), {
                    variant: 'warning',
                    anchorOrigin: {
                        vertical: 'top',
                        horizontal: 'right',
                    },
                });
            }
        }
    };

    const actionButton = (url: string) => (
        <IconButton onClick={() => history.push(url)}>
            <ExternalLink />
        </IconButton>
    );

    const conversationsAlert = (
        payload: TPayload,
        options?: TNotificationOptions
    ) => {
        const { conversationId, area } = payload;
        const title = options?.title;

        playNotificationSound();

        enqueueSnackbar(title, {
            ...getSnackbarProps(options),
            action: actionButton(
                `/conversations?id=${conversationId}&area=${area}`
            ),
        });
    };

    const ticketAlert = (
        payload: TTicketPayload,
        options?: TNotificationOptions
    ) => {
        const { ticketId } = payload;
        const title = options?.title;

        playNotificationSound();

        enqueueSnackbar(title, {
            ...getSnackbarProps(options),
            action: actionButton(`/management/tickets/${ticketId}`),
        });
    };

    const customAlert = (
        message: string,
        variant: VariantType = 'default',
        options: OptionsObject = {},
        actionUrl?: string,
        playSound = true
    ) => {
        let optionsWithAction = options;
        if (actionUrl) {
            optionsWithAction.action = actionButton(actionUrl);
        }
        if (playSound) {
            playNotificationSound();
        }
        enqueueSnackbar(message, {
            variant,
            ...optionsWithAction,
        });
    };

    return {
        conversationsAlert,
        ticketAlert,
        customAlert,
    };
};
